// Account / usage view (spec 18 §9).
//
// Reads GET /api/users/me/usage and renders:
//   - plan summary (id, period dates)
//   - per-metric usage bar (used / limit)

import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { GlassPanel, Pill } from "@/components/glass";
import { analytics } from "@/lib/analytics";

interface UsageMetric {
	used: number;
	limit: number | null;
	label: string;
	unit: string;
	behaviour: string | null;
}

interface UsageResponse {
	plan: {
		id: string;
		price_usd_monthly: number;
		period_start: number;
		period_end: number;
	};
	metrics: Record<string, UsageMetric>;
}

interface MyBilling {
	plan: { id: string; lockedByAdmin: boolean };
	subscription: { status: string; periodEnd: string | null } | null;
	portalUrl: string | null;
}

async function fetchUsage(): Promise<UsageResponse> {
	const r = await fetch("/api/users/me/usage", { credentials: "include" });
	if (!r.ok) throw new Error(`usage: ${r.status}`);
	return r.json();
}

async function fetchBilling(): Promise<MyBilling> {
	const r = await fetch("/api/billing/me", { credentials: "include" });
	if (!r.ok) throw new Error(`billing: ${r.status}`);
	return r.json();
}

export default function UsageView() {
	const { data, isLoading, error } = useQuery({
		queryKey: ["account-usage"],
		queryFn: fetchUsage,
		refetchInterval: 60_000, // refresh once a minute while page is open
	});

	const billingQ = useQuery({
		queryKey: ["billing-me"],
		queryFn: fetchBilling,
	});

	if (isLoading) {
		return <div className="p-8 text-sm opacity-60">Loading usage…</div>;
	}
	if (error || !data) {
		return (
			<div className="p-8 text-sm text-red-400">
				Failed to load usage: {(error as Error | undefined)?.message ?? "unknown"}
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-3xl space-y-6 p-6">
			<header className="space-y-1">
				<h1 className="text-2xl font-medium tracking-tight">Usage</h1>
				<p className="text-sm opacity-60">
					Current period:{" "}
					{new Date(data.plan.period_start).toLocaleDateString()} —{" "}
					{new Date(data.plan.period_end).toLocaleDateString()}
				</p>
			</header>

			<GlassPanel className="p-5 space-y-4">
				<div className="flex items-baseline justify-between">
					<div>
						<div className="text-xs uppercase tracking-wide opacity-50">
							Plan
						</div>
						<div className="text-lg font-medium capitalize">{data.plan.id}</div>
					</div>
					<div className="text-right">
						<div className="text-xs uppercase tracking-wide opacity-50">
							Price
						</div>
						<div className="text-lg font-medium">
							${data.plan.price_usd_monthly.toFixed(2)}
							<span className="text-xs opacity-50">/mo</span>
						</div>
					</div>
				</div>
				<BillingActions
					currentPlanId={data.plan.id}
					billing={billingQ.data ?? null}
				/>
			</GlassPanel>

			<section className="space-y-3">
				<h2 className="text-xs uppercase tracking-wide opacity-50">
					Quotas this period
				</h2>
				<GlassPanel className="divide-y divide-white/5 p-0">
					{Object.entries(data.metrics).map(([key, m]) => (
						<MetricRow key={key} metric={m} />
					))}
				</GlassPanel>
			</section>

			{/* by-model breakdown intentionally hidden from the user view —
			    it leaks model names + vendor architecture. Data is still
			    captured in usage_event and reachable via admin. */}
		</div>
	);
}

function MetricRow({ metric }: { metric: UsageMetric }) {
	const pct =
		metric.limit && metric.limit > 0
			? Math.min(100, (metric.used / metric.limit) * 100)
			: 0;
	const over = metric.limit != null && metric.used > metric.limit;
	return (
		<div className="space-y-1.5 px-5 py-3">
			<div className="flex items-baseline justify-between text-sm">
				<span>{metric.label}</span>
				<span className="font-mono text-xs opacity-70">
					{formatMetricValue(metric.used, metric.unit)}
					{metric.limit != null && (
						<>
							{" / "}
							{formatMetricValue(metric.limit, metric.unit)}
						</>
					)}
				</span>
			</div>
			{metric.limit != null && (
				<div className="h-1.5 overflow-hidden rounded-full bg-white/5">
					<div
						className={`h-full rounded-full transition-all ${
							over ? "bg-red-400" : pct > 80 ? "bg-amber-400" : "bg-white/40"
						}`}
						style={{ width: `${pct}%` }}
					/>
				</div>
			)}
		</div>
	);
}

function formatMetricValue(v: number, unit: string): string {
	if (unit === "usd") return `$${v.toFixed(2)}`;
	if (unit === "bytes") {
		if (v >= 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
		if (v >= 1024) return `${(v / 1024).toFixed(1)} KB`;
		return `${v} B`;
	}
	return v.toLocaleString();
}


function BillingActions({
	currentPlanId,
	billing,
}: {
	currentPlanId: string;
	billing: MyBilling | null;
}) {
	const isPaid = currentPlanId === "pro";
	if (!isPaid) {
		// Free tier: show the upgrade pitch.
		return (
			<div className="flex items-center justify-between gap-3 pt-2 border-t border-[var(--color-border)]">
				<div className="text-[12px] text-[var(--color-text-muted)]">
					Need more compute or storage? See the paid tiers.
				</div>
				<Link to="/pricing">
					<Pill>
						<ArrowUpRight className="w-3.5 h-3.5" />
						Upgrade
					</Pill>
				</Link>
			</div>
		);
	}

	// Paid tier: manage subscription (and show subscription status when
	// it's worth flagging — past_due or trialing).
	const status = billing?.subscription?.status;
	const flagged = status === "past_due" || status === "trialing";
	return (
		<div className="flex items-center justify-between gap-3 pt-2 border-t border-[var(--color-border)]">
			<div className="text-[12px] text-[var(--color-text-muted)]">
				{flagged ? (
					<span className="text-[var(--color-warning,#f59e0b)]">
						Subscription {status}
					</span>
				) : (
					"Managed through Whop. Cancel or change plan anytime."
				)}
			</div>
			{billing?.portalUrl ? (
				<a
					href={billing.portalUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => analytics.capture("billing_portal_opened", {})}
				>
					<Pill>
						<ExternalLink className="w-3.5 h-3.5" />
						Manage subscription
					</Pill>
				</a>
			) : (
				<Link to="/pricing">
					<Pill>
						<ArrowUpRight className="w-3.5 h-3.5" />
						See plans
					</Pill>
				</Link>
			)}
		</div>
	);
}
